import { useState, useEffect } from 'react';
import { Thread } from './chat/Thread';
import { useCustomLangGraphRuntime } from '../services/langGraphRuntime';
import { shortid } from '../services/id.service';
import { ConversationHistory } from './chat/ConversationHistory';
import { AdvancedThreadControls, useAdvancedThreadControls } from './chat/AdvancedThreadControls';
import { ThreadSettingsContext } from './chat/threadSettings';

const DEFAULT_RUTHIE_MODEL = 'claude-opus-4-6';

interface RuthieProps {
  initialQuestion?: string;
  showHistory?: boolean;
  conversationId?: string;
}

// Wrapper component that renders the assistant with a specific thread ID
const AssistantWrapper = ({
  threadId,
  initialQuestion,
  onPlanExecuted,
}: {
  threadId: string;
  initialQuestion: string;
  onPlanExecuted: (result: unknown) => void;
}) => {
  const {
    controlsEnabled,
    showControls,
    settings,
    applyLoadedSettings,
    handleSettingsChange,
  } = useAdvancedThreadControls({
    threadId,
    defaultModel: DEFAULT_RUTHIE_MODEL,
    // Ruthie is a builder-facing agent — narrating its work is useful
    defaultVerbosity: 'high',
  });

  const { runtime, isHistoricalThread, isLoaded, accessDenied, resumeGraph, compactConversation, messageCount, threadSettings } = useCustomLangGraphRuntime({
    agentType: 'data_architect',
    model: settings.model,
    verbosity: settings.verbosity,
    reasoning: settings.reasoning,
    threadId,
  });

  useEffect(() => {
    applyLoadedSettings(threadSettings);
  }, [threadSettings, applyLoadedSettings]);

  // Auto-run for new threads only
  // Historical threads should start collapsed to avoid loading all at once
  const autoRunEnabled = !isHistoricalThread;

  // Show loading state while loading historical thread
  if (isHistoricalThread && !isLoaded) {
    return (
      <div className="p-4">
        <p>Loading conversation...</p>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="p-4">
        <p className="font-medium">You don't have permission to view this conversation.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {controlsEnabled && (
        <AdvancedThreadControls
          open={showControls}
          settings={settings}
          onSettingsChange={handleSettingsChange}
          threadId={threadId}
        />
      )}
      <div className="flex-1 min-h-0">
        <ThreadSettingsContext.Provider value={settings}>
          <Thread
            runtime={runtime}
            initialQuestion={initialQuestion}
            toolCallHandlers={{
              onPlanExecuted,
              onResumeGraph: resumeGraph,
            }}
            autoRunCharts={autoRunEnabled}
            emptyStateMessage="Hi, I'm Ruthie! I can help you build and modify your semantic layer in Pliable's develop mode. Need to create metrics, define relationships, or set up dimensions? Just ask!"
            onCompact={compactConversation}
            messageCount={messageCount}
          />
        </ThreadSettingsContext.Provider>
      </div>
    </div>
  );
};

export const Ruthie: React.FC<RuthieProps> = ({
  initialQuestion = '',
  showHistory = false,
  conversationId,
}) => {
  // Use conversationId if provided, otherwise generate a new thread ID
  const [defaultThreadId, setDefaultThreadId] = useState(() => shortid());
  const currentThreadId = conversationId || defaultThreadId;

  const [executedPlans, setExecutedPlans] = useState<string[]>([]);

  const handleNewChat = () => {
    setDefaultThreadId(shortid());
    setExecutedPlans([]);
  };

  const content = (
    <AssistantWrapper
      key={currentThreadId}
      threadId={currentThreadId}
      initialQuestion={initialQuestion}
      onPlanExecuted={(result) => {
        // Track executed plans if needed
        if (result && typeof result === 'object' && 'success' in result) {
          const planResult = result as { success: boolean };
          if (planResult.success) {
            setExecutedPlans([...executedPlans, currentThreadId]);
          }
        }
      }}
    />
  );

  if (showHistory) {
    return (
      <div className="h-full w-full flex">
        <div className="flex-[3] min-w-0">
          {content}
        </div>
        <div className="flex-1 min-w-0">
          <ConversationHistory
            activeThreadId={currentThreadId}
            onNewChat={handleNewChat}
            agentType="data_architect"
            baseUrl="/ruthie"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {content}
    </div>
  );
};
